import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import {
  AlertTriangle,
  FileText,
  ArrowUpRight,
  CheckCircle2,
  Copy,
  FileUp,
  HelpCircle,
  MessageSquare,
  RotateCcw,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from './components/Button';
import type { FooterHelp } from './types';
import { Progress } from './components/Progress';

/**
 * Full-height centered screen (connecting / fatal errors).
 *
 * `gap` defaults to pdf's success screen (2.5); its connecting screen is tighter at 2, so
 * that one passes its own. px is pdf's 5, not 6.
 */
export function CenteredScreen({ children, gap = '2.5' }: { children: ReactNode; gap?: string }) {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap={gap}
      h="100%"
      px="5"
      textAlign="center"
    >
      {children}
    </Flex>
  );
}

export function ConnectingScreen() {
  return (
    <CenteredScreen gap="2">
      <Spinner color="figma.accent" size="sm" />
      {/* pdf's wording, so the family says one thing rather than twelve. */}
      <Text fontSize="2xs" color="figma.textMuted">
        Just a moment…
      </Text>
    </CenteredScreen>
  );
}

export function ConnectionErrorScreen({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <CenteredScreen>
      <Box color="figma.warning">
        <AlertTriangle size={28} />
      </Box>
      <Text fontSize="xs" color="figma.textSecondary" maxW="320px">
        {message}
      </Text>
      <Button colorPalette="gray" variant="outline" onClick={onRetry}>
        Try again
      </Button>
    </CenteredScreen>
  );
}


/** One row of the help menu. */
function MenuItem({
  icon,
  label,
  suffix,
  onClick,
}: {
  icon?: ReactNode;
  label: string;
  suffix?: ReactNode;
  onClick: () => void;
}) {
  return (
    <Flex
      as="button"
      onClick={onClick}
      align="center"
      gap="2"
      w="full"
      px="3"
      py="1.5"
      fontSize="xs"
      color="figma.text"
      textAlign="left"
      _hover={{ bg: 'figma.hover' }}
      transition="background 0.1s"
    >
      {icon ? (
        <Box color="figma.textMuted" display="flex" alignItems="center">
          {icon}
        </Box>
      ) : (
        /* Keeps the labels of icon-less rows on the same left edge as the rest. */
        <Box w="11px" flexShrink={0} />
      )}
      <Text flex="1">{label}</Text>
      {suffix ? (
        <Box color="figma.textMuted" display="flex" alignItems="center">
          {suffix}
        </Box>
      ) : null}
    </Flex>
  );
}

/**
 * The strip along the bottom: the plugin's name, and the help menu behind the `?`.
 *
 * THE MENU IS HERE RATHER THAN IN EACH APP because every importer wants the same three things
 * and was getting a different subset of them — pdf-to-figma had all three, photoshop-to-figma
 * had its own, and the apps on this shared flow had none at all, so illustrator and eps showed
 * a bare label where every other plugin in the family shows a menu.
 */
export function Footer({ label, help }: { label?: string | undefined; help?: FooterHelp }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Click-away. Bound only while open, so a closed menu costs no document listener.
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    [],
  );

  const copyId = useCallback(() => {
    const text = help?.userId || 'unknown';
    /*
     * `navigator.clipboard` is unavailable in Figma's sandboxed iframe — the plugin UI is
     * served from an opaque origin, and the Clipboard API requires a secure, permitted one. A
     * hidden textarea plus `execCommand` is deprecated everywhere and is the only thing that
     * works here; `window.prompt` is the last resort, which at least lets someone select the
     * id by hand.
     */
    try {
      const area = document.createElement('textarea');
      area.value = text;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      /*
       * Deprecated everywhere, and the only thing that copies here: the Clipboard API needs a
       * secure, permitted origin, and a plugin UI is served into a sandboxed iframe from an
       * opaque one. The directive has to sit on the line immediately above the call.
       */
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      document.execCommand('copy');
      document.body.removeChild(area);
      setCopied(true);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('Copy your user ID:', text);
    }
  }, [help?.userId]);

  const hasMenu =
    help !== undefined &&
    (help.userId !== undefined ||
      help.onSendFeedback !== undefined ||
      help.onManageLicense !== undefined);

  return (
    <Box as="footer" position="relative" flexShrink={0}>
      <Flex
        px="4"
        py="2"
        align="center"
        justify="space-between"
        borderTopWidth="1px"
        borderColor="figma.border"
        bg="figma.bgDark"
      >
        {/*
          Usually nothing. Figma's own window chrome already shows the plugin's name directly
          above this strip, so printing it again here said it twice — three times with the
          header that used to sit under the chrome. The prop stays for a plugin that has
          something else to say down here; absent, the `?` keeps its corner because the row is
          still `space-between`.
        */}
        {label ? (
          <Text fontSize="2xs" color="figma.textMuted">
            {label}
          </Text>
        ) : (
          <Box />
        )}
        {hasMenu ? (
          <Box
            as="button"
            aria-label="Help"
            aria-expanded={open}
            onClick={() => setOpen((wasOpen) => !wasOpen)}
            display="flex"
            alignItems="center"
            p="1"
            borderRadius="sm"
            color={open ? 'figma.text' : 'figma.textMuted'}
            _hover={{ color: 'figma.text' }}
            transition="color 0.15s"
          >
            <HelpCircle size={13} />
          </Box>
        ) : null}
      </Flex>

      {hasMenu && open ? (
        <Box
          ref={popoverRef}
          position="absolute"
          bottom="32px"
          right="2"
          w="200px"
          bg="figma.panel"
          borderWidth="1px"
          borderColor="figma.border"
          borderRadius="md"
          boxShadow="lg"
          overflow="hidden"
          zIndex="100"
        >
          <Box py="0.5">
            {help?.userId !== undefined ? (
              <MenuItem
                icon={<Copy size={11} />}
                label={copied ? 'Copied!' : 'Copy User ID'}
                onClick={copyId}
              />
            ) : null}
            {help?.onSendFeedback !== undefined ? (
              <MenuItem
                icon={<MessageSquare size={11} />}
                label="Send feedback"
                suffix={<ArrowUpRight size={9} />}
                onClick={() => {
                  help.onSendFeedback?.();
                  setOpen(false);
                }}
              />
            ) : null}
            {help?.onManageLicense !== undefined ? (
              <MenuItem
                label="Manage license"
                onClick={() => {
                  help.onManageLicense?.();
                  setOpen(false);
                }}
              />
            ) : null}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

/** Sticky error/notice banner shown above the content. */
export function Banner({
  text,
  tone = 'error',
  onDismiss,
}: {
  text: string;
  tone?: 'error' | 'warning';
  onDismiss?: () => void;
}) {
  const bg = tone === 'error' ? 'figma.errorMuted' : 'figma.warningMuted';
  const fg = tone === 'error' ? 'figma.error' : 'figma.warning';
  return (
    <Flex
      align="flex-start"
      gap="2"
      px="4"
      py="2.5"
      bg={bg}
      borderBottomWidth="1px"
      borderColor="figma.border"
    >
      <Box color={fg} mt="0.5" flexShrink={0}>
        <AlertTriangle size={14} />
      </Box>
      <Text flex="1" fontSize="xs" color="figma.text">
        {text}
      </Text>
      {onDismiss && (
        <Box
          as="button"
          color="figma.textSecondary"
          _hover={{ color: 'figma.text' }}
          onClick={onDismiss}
          flexShrink={0}
        >
          <X size={14} />
        </Box>
      )}
    </Flex>
  );
}

export function UploadScreen({
  accept,
  dropHint,
  subhint,
  onFile,
}: {
  accept: string;
  dropHint: string;
  subhint?: string | undefined;
  onFile: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  return (
    <Box h="100%" p="3">
      <Flex
        as="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e: React.DragEvent<HTMLElement>) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e: React.DragEvent<HTMLElement>) => {
          // Ignore dragleave bubbling up from inner children (icon/text), which
          // would otherwise strobe the highlight as the pointer crosses them.
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setDragging(false);
          }
        }}
        onDrop={(e: React.DragEvent<HTMLElement>) => {
          e.preventDefault();
          setDragging(false);
          const file = e.dataTransfer?.files[0];
          if (file) onFile(file);
        }}
        direction="column"
        align="center"
        justify="center"
        h="100%"
        w="full"
        px="4"
        borderWidth="1px"
        borderStyle="dashed"
        borderColor={dragging ? 'figma.accent' : 'figma.border'}
        bg={dragging ? 'figma.accentMuted' : 'transparent'}
        borderRadius="md"
        cursor="pointer"
        textAlign="center"
        transition="border-color 0.15s, background 0.15s"
        _hover={{ borderColor: 'figma.borderStrong', bg: 'figma.bgSubtle' }}
      >
        <Flex
          align="center"
          justify="center"
          w="44px"
          h="44px"
          borderRadius="full"
          bg={dragging ? 'figma.accentMuted' : 'figma.panel'}
          color={dragging ? 'figma.accent' : 'figma.textSecondary'}
          transition="background 0.15s"
          mb="3"
        >
          <FileUp size={20} strokeWidth={1.5} />
        </Flex>
        <Text fontSize="md" fontWeight="500" color="figma.text" letterSpacing="snug">
          {dragging ? 'Release to import' : dropHint}
        </Text>
        {!dragging && subhint && (
          <Text fontSize="xs" color="figma.textSecondary" mt="1.5" maxW="240px" lineHeight="1.5">
            {subhint}
          </Text>
        )}
        {!dragging && (
          <Text mt="3" fontSize="xs" fontWeight="500" color="figma.accent">
            Choose a file
          </Text>
        )}
      </Flex>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => {
          // currentTarget, not target: Preact types `target` as a bare
          // EventTarget, and it's the input either way — `target` can be a
          // descendant, `currentTarget` is what the handler is on.
          const input = e.currentTarget;
          const file = input.files?.[0];
          if (file) onFile(file);
          input.value = '';
        }}
      />
    </Box>
  );
}

export function ProcessingScreen({
  fileName,
  message,
  progress,
  detail,
  percent,
  onCancel,
}: {
  fileName: string;
  message: string | null;
  progress: { completed: number; total: number } | null;
  /**
   * The line under the step, when the caller can say more than "3/12 pages" — pdf adds an
   * elapsed or remaining time, e.g. "3/12 pages · ~8s left", which it is the only one with
   * the numbers to compute. Omitted, the page count is used.
   */
  detail?: string | undefined;
  /**
   * The bar's position, 0-100, when pages done over pages total is not the real answer.
   * pdf's import keeps going after the last page is parsed — fonts, then handing the
   * document to the sandbox — so its bar sits at 95 while the page count already reads
   * 1/1. Without this the bar would claim finished with a third of the wait left.
   */
  percent?: number | undefined;
  onCancel: () => void;
}) {
  const pct =
    percent ??
    (progress && progress.total > 0 ? (progress.completed / progress.total) * 100 : 0);
  // pdf's detail line, e.g. "3/12 pages". The message is shown above it in its own right,
  // so this no longer falls back to it.
  const label =
    detail ?? (progress && progress.total > 0 ? `${progress.completed}/${progress.total} pages` : '');

  /*
   * pdf's loading screen, which is a different shape from what this was.
   *
   * It was a left-aligned full-width stack — file name in bold, message under it, a
   * labelled bar, Cancel pushed to the right. pdf centres a 200px column: the bar and the
   * percentage share one row, the step reads under it, then the detail, then Cancel in the
   * middle. The file name lives in a header strip rather than in the body.
   *
   * The strip is reproduced here because ImporterFlow has no header of its own, and
   * dropping the name outright would lose something the eleven importers currently show.
   * Its styling is pdf's own header label: 36px tall, 2xs, textSecondary, hairline rule.
   */
  return (
    <Flex direction="column" h="100%">
      <Flex
        h="36px"
        px="3"
        align="center"
        flexShrink={0}
        borderBottomWidth="1px"
        borderColor="figma.border"
      >
        <Text fontSize="2xs" color="figma.textSecondary" truncate>
          {fileName}
        </Text>
      </Flex>

      <Flex flex="1" align="center" justify="center" px="5">
        <Box w="full" maxW="200px">
          <Flex align="center" gap="2" mb="2.5">
            <Box flex="1">
              <Progress value={pct} size="xs" />
            </Box>
            <Text
              fontSize="2xs"
              fontWeight="600"
              fontFamily="mono"
              color="figma.accent"
              flexShrink={0}
            >
              {Math.round(pct)}%
            </Text>
          </Flex>

          <Text
            fontSize="xs"
            fontWeight="500"
            color="figma.text"
            textAlign="center"
            lineHeight="1.4"
          >
            {message ?? 'Importing…'}
          </Text>

          {!!label && (
            <Text fontSize="2xs" color="figma.textMuted" textAlign="center" mt="0.5">
              {label}
            </Text>
          )}

          <Flex justify="center" mt="3">
            <Button variant="ghost" colorPalette="gray" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
}

export function SuccessScreen({
  message,
  onReset,
}: {
  message: string;
  onReset: () => void;
}) {
  return (
    <CenteredScreen>
      <Box color="figma.success">
        <CheckCircle2 size={32} />
      </Box>
      <Text fontSize="md" fontWeight="600" color="figma.text">
        Imported into Figma
      </Text>
      <Text fontSize="2xs" color="figma.textMuted" maxW="320px">
        {message}
      </Text>
      <Button colorPalette="accent" onClick={onReset}>
        <RotateCcw size={14} /> Import another
      </Button>
    </CenteredScreen>
  );
}

export function ErrorScreen({
  message,
  onReset,
}: {
  message: string;
  onReset: () => void;
}) {
  return (
    <CenteredScreen>
      <Box color="figma.error">
        <AlertTriangle size={28} />
      </Box>
      <Text fontSize="xs" color="figma.textSecondary" maxW="320px">
        {message}
      </Text>
      <Button colorPalette="gray" variant="outline" onClick={onReset}>
        Start over
      </Button>
    </CenteredScreen>
  );
}

/** The settings screen: file name bar + app settings + Import action. */
/**
 * The settings view, copied from pdf-to-figma's import sheet so the family shares one layout.
 *
 * Three parts, in the order the screenshots show them: a FILE BAR naming what is about to be
 * imported, a TWO-COLUMN BODY with an optional preview strip beside the controls, and a FOOTER
 * carrying what the import will cost beside the buttons that start it.
 *
 * `preview` and `summary` are optional because the apps differ in what they can say. A PDF
 * knows it has 22 pages and roughly how long they take; a PSD is one document, and a footer
 * that said "Importing 1 document" would be noise. Left out, those parts are absent rather
 * than empty — the layout closes up instead of showing a gap where a number should be.
 */
export function SettingsScreen({
  fileName,
  fileMeta,
  preview,
  settings,
  summary,
  subSummary,
  secondaryAction,
  importLabel,
  importDisabled = false,
  onChangeFile,
  onImport,
}: {
  fileName: string;
  /** Short right-aligned note in the file bar, e.g. "22 pgs". */
  fileMeta?: string | undefined;
  /** The scrolling strip to the left of the controls — page thumbnails, a document preview. */
  preview?: ReactNode;
  settings: ReactNode;
  /** What is about to happen, e.g. "Importing 22 pages". */
  summary?: string | undefined;
  /** A quieter second line under it, e.g. "About 48s". */
  subSummary?: string | undefined;
  /**
   * An alternative to the primary button, under the summary. Shown instead of `subSummary`,
   * because the two occupy one line and an offer beats a restatement of the problem.
   */
  secondaryAction?: { label: string; onClick: () => void } | undefined;
  importLabel: string;
  /** Import is unavailable — an empty selection, typically. Cancel stays live. */
  importDisabled?: boolean | undefined;
  onChangeFile: () => void;
  onImport: () => void;
}) {
  return (
    <Flex direction="column" h="100%" minH="0" overflow="hidden">
      {/* File bar */}
      <Flex
        align="center"
        gap="2"
        px="3"
        h="34px"
        minH="34px"
        borderBottomWidth="1px"
        borderColor="figma.border"
        flexShrink={0}
      >
        <Box color="figma.textMuted" display="flex" alignItems="center" flexShrink={0}>
          <FileText size={11} />
        </Box>
        <Text
          fontSize="2xs"
          fontWeight="500"
          color="figma.text"
          flex="1"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {fileName}
        </Text>
        {fileMeta ? (
          <Text fontSize="2xs" color="figma.textMuted" flexShrink={0}>
            {fileMeta}
          </Text>
        ) : null}
        <Box
          as="button"
          aria-label="Choose a different file"
          onClick={onChangeFile}
          p="0.5"
          borderRadius="sm"
          color="figma.textMuted"
          _hover={{ color: 'figma.text', bg: 'figma.panelHover' }}
          transition="all 0.15s"
          flexShrink={0}
        >
          <X size={11} />
        </Box>
      </Flex>

      {/*
        Two-column body. The preview is a strip the designer scrolls independently of the form,
        so they are not made to choose between seeing the document and seeing the settings.
      */}
      <Flex flex="1" minH="0" overflow="hidden">
        {preview ? (
          <Box
            w="124px"
            minW="124px"
            flexShrink={0}
            overflowY="auto"
            borderRightWidth="1px"
            borderColor="figma.border"
            py="2"
            px="2"
            // pdf's rail is tinted and carries a thin scrollbar — a 124px column with the
            // page's own background reads as part of the form rather than beside it.
            bg="figma.bgSubtle"
            css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255,255,255,0.12)',
                borderRadius: '999px',
              },
            }}
          >
            {preview}
          </Box>
        ) : null}
        <Box flex="1" minW="0" overflowY="auto" px="3" py="2.5">
          {settings}
        </Box>
      </Flex>

      {/* Footer: what it will cost on the left, what starts it on the right. */}
      <Flex
        align="center"
        justify="space-between"
        gap="2"
        px="3"
        py="2"
        borderTopWidth="1px"
        borderColor="figma.border"
        flexShrink={0}
      >
        <Box minW="0">
          {summary ? (
            <Text fontSize="2xs" color="figma.textSecondary" lineHeight="1.4">
              {summary}
              {secondaryAction ? (
                /*
                 * The way out that is not the upgrade, INLINE in the sentence that explains
                 * why it is needed — "…needs Pro. Import 1 page free" reads as one thought,
                 * where a line of its own reads as an unrelated second offer. pdf has always
                 * written it this way.
                 *
                 * A link rather than a button: it is an alternative to Import, not a peer.
                 */
                <>
                  {' '}
                  <Box
                    as="button"
                    display="inline"
                    color="figma.accent"
                    fontWeight="500"
                    _hover={{ color: 'figma.accentHover' }}
                    onClick={secondaryAction.onClick}
                  >
                    {secondaryAction.label}
                  </Box>
                </>
              ) : null}
            </Text>
          ) : null}
          {subSummary ? (
            <Text fontSize="9px" color="figma.textMuted" mt="0.5" truncate>
              {subSummary}
            </Text>
          ) : null}
        </Box>
        <Flex align="center" gap="1.5" flexShrink={0}>
          <Button variant="ghost" colorPalette="gray" size="sm" onClick={onChangeFile}>
            Cancel
          </Button>
          <Button colorPalette="accent" size="sm" onClick={onImport} disabled={importDisabled}>
            {importLabel}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}
